import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import {
  STATUTORY_PAYMENT_OPTION,
  STATUTORY_PAYMENT_STATUS_OPTION,
  STATUTORY_TYPE,
} from '@nxsys/constants/statutory';
import { getRangeYear } from '@nxsys/utils/taxYear';
import { hasFilterValue } from '@nxsys/utils/filterForm';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import { orderBy } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

type FilterModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  onFilter: (params: any) => void;
  type: (typeof STATUTORY_TYPE)[keyof typeof STATUTORY_TYPE];
};

const FilterModal: React.FC<FilterModalProps> = ({
  title,
  isOpen,
  toggle,
  onFilter,
  type = STATUTORY_TYPE.LEAVE,
}: FilterModalProps) => {
  const { isAdmin, isAgency, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const taxYearRange = orderBy(getRangeYear(isIreland), 'value', 'desc');

  const { statutoryLeaveData, statutoryPaymentData } = useSelector(
    (state: AppState) => state.statutoryStore,
  );

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const hasFilter = hasFilterValue(watchValue);
  // Keep Apply enabled once a filter is applied, so Clear All + Apply can remove it.
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const taxYearOptions = taxYearRange.map((option) => ({
    label: option.label,
    value: option.value.toString(),
  }));

  const filterOptions = [
    {
      label: 'Tax Year',
      name: 'taxYear',
      filterType: 'select',
      options: taxYearOptions,
      placeholder: 'Select tax year',
      defaultValue: undefined,
    },
    ...(type === STATUTORY_TYPE.PAYMENT
      ? [
          {
            name: 'taxPeriod',
            children: [
              {
                label: 'Tax Period From',
                name: 'taxPeriodFrom',
                filterType: 'number',
              },
              {
                label: 'Tax Period To',
                name: 'taxPeriodTo',
                filterType: 'number',
              },
            ],
          },
        ]
      : []),
    ...(isAdmin || isAgency
      ? [
          {
            label: 'Payment Company',
            name: 'companyList',
            filterType: 'selectSync',
            selectSyncType: 'company',
            apiType: 'searchCompany',
            placeholder: 'Select payment company',
          },
        ]
      : []),
    ...(!isAgency
      ? [
          {
            label: 'Agency',
            name: 'agencyList',
            filterType: 'selectSync',
            selectSyncType: 'agency',
            apiType: 'searchAgency',
            placeholder: 'Select agency',
          },
        ]
      : []),
    {
      label: 'Leave Type',
      name: 'type',
      filterType: 'select',
      options: STATUTORY_PAYMENT_OPTION,
      placeholder: 'Select leave type',
    },
    {
      label: 'Pay Frequency',
      name: 'payFrequency',
      filterType: 'select',
      options: defaultPaymentFrequency,
      placeholder: 'Select pay frequency',
    },
    ...(type === STATUTORY_TYPE.LEAVE
      ? [
          {
            label: 'Diary Status',
            name: 'status',
            filterType: 'select',
            options: STATUTORY_PAYMENT_STATUS_OPTION,
            placeholder: 'Select diary status',
          },
        ]
      : []),
  ];

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    const params = {
      page: 0,
      ...(type === STATUTORY_TYPE.LEAVE && {
        ...statutoryLeaveData?.queryParams,
        status: values?.status,
      }),
      ...(type === STATUTORY_TYPE.PAYMENT && {
        ...statutoryPaymentData?.queryParams,
        taxPeriodFrom: values?.taxPeriodFrom,
        taxPeriodTo: values?.taxPeriodTo,
      }),
      taxYear: values?.taxYear,
      companyIds: values?.companyList?.map((c: any) => c.value),
      agencyIds: values?.agencyList?.map((a: any) => a.value),
      type: values?.type,
      payFrequency: values?.payFrequency,
    };
    setHasAppliedFilter(hasFilterValue(values));
    onFilter(params);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: 'Cancel',
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: 'Apply',
          buttonType: 'primary',
          onClick: () => form.submit(),
          disabled: !hasFilter && !hasAppliedFilter,
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name="Clear All"
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!hasFilter}
          />
        </div>
      }
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {filterOptions?.map((f) => {
          const hasChildren = f.children && Array.isArray(f.children);

          return (
            <Form.Item
              key={f.name}
              label={f.label}
              name={f.name}
              layout="vertical"
              {...(hasChildren ? { style: { marginBottom: 0 } } : {})}
            >
              {f.filterType === 'select' && (
                <AppSelect
                  placeholder={f.placeholder}
                  options={f.options ?? []}
                  onChange={(value) => {
                    form.setFieldsValue({
                      [f.name]: value.value,
                    });
                  }}
                  defaultValue={f.defaultValue}
                  allowClear
                />
              )}
              {f.filterType === 'selectSync' && (
                <AppSelectSync
                  type={f.selectSyncType ?? ''}
                  apiType={f.apiType as any}
                  placeholder={f.placeholder}
                  onChangeValue={(value: OptionType | OptionType[] | null) => {
                    form.setFieldsValue({
                      [f.name]: value,
                    });
                  }}
                  mode={'multiple'}
                />
              )}
              {hasChildren && (
                <Row gutter={8}>
                  {f.children.map((child: any) => (
                    <Col key={child.name} span={12}>
                      <Form.Item
                        label={child.label}
                        name={child.name}
                        layout="vertical"
                      >
                        {child.filterType === 'number' && (
                          <AppInput type={child.filterType} />
                        )}
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              )}
            </Form.Item>
          );
        })}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default FilterModal;
